import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, LogIn, LogOut, RefreshCw, MonitorCheck } from "lucide-react";

export default function KioskPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      toast.error("Camera error: " + e.message);
    }
  };
  const stop = () => { if (stream) stream.getTracks().forEach((t) => t.stop()); setStream(null); };
  useEffect(() => { start(); return () => stop(); /* eslint-disable-next-line */ }, []);

  const snap = () => {
    if (!videoRef.current) return null;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth || 480;
    canvas.height = videoRef.current.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.7);
  };

  const capture = () => {
    const d = snap();
    if (d) setPhoto(d);
  };

  const submit = async (action) => {
    if (!email) return toast.error("Enter staff email");
    if (!photo) return toast.error("Capture face first");
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/staff", {
        method: "kiosk_face",
        action,
        photo_data_url: photo,
        kiosk_user_email: email,
      });
      setLastEvent({ name: data.attendance.user_name, role: data.attendance.user_role, action, at: new Date().toLocaleTimeString() });
      toast.success(`${action === "check_in" ? t("checkIn") : t("checkOut")} — ${data.attendance.user_name}`);
      setEmail("");
      setPhoto(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kiosk-shell min-h-screen text-white">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2xl bg-emerald-500 text-emerald-950">
              <MonitorCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-2xl font-bold">Office Kiosk</div>
              <div className="text-xs text-white/60">Operated by {user?.name}</div>
            </div>
          </div>
          <div className="num text-sm text-white/60">{new Date().toLocaleString()}</div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
          <div className="rounded-3xl overflow-hidden bg-black/60 aspect-video relative">
            {photo ? (
              <img src={photo} className="h-full w-full object-cover" alt="captured" />
            ) : (
              <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-cover" />
            )}
          </div>
          <div className="space-y-4">
            <Card className="rounded-3xl bg-white/5 border-white/10 text-white">
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/80">Staff {t("email")}</Label>
                  <Input
                    data-testid="kiosk-email"
                    className="h-14 text-lg bg-white text-slate-900"
                    placeholder="teacher@school.my"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {photo ? (
                    <Button
                      variant="secondary"
                      onClick={() => { setPhoto(null); }}
                      className="h-14 rounded-full text-base col-span-2 gap-2"
                      data-testid="kiosk-retake"
                    >
                      <RefreshCw className="h-4 w-4" /> {t("recapture")}
                    </Button>
                  ) : (
                    <Button
                      onClick={capture}
                      className="h-14 rounded-full text-base col-span-2 gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950"
                      data-testid="kiosk-capture"
                    >
                      <Camera className="h-4 w-4" /> {t("captureFace")}
                    </Button>
                  )}
                  {photo && (
                    <>
                      <Button
                        onClick={() => submit("check_in")}
                        disabled={busy}
                        className="h-14 rounded-full text-base gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950"
                        data-testid="kiosk-checkin"
                      >
                        <LogIn className="h-4 w-4" /> {t("checkIn")}
                      </Button>
                      <Button
                        onClick={() => submit("check_out")}
                        disabled={busy}
                        variant="secondary"
                        className="h-14 rounded-full text-base gap-2"
                        data-testid="kiosk-checkout"
                      >
                        <LogOut className="h-4 w-4" /> {t("checkOut")}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            {lastEvent && (
              <Card className="rounded-3xl bg-emerald-500/10 border-emerald-500/30 text-white" data-testid="kiosk-last">
                <CardContent className="p-6">
                  <div className="text-sm text-white/70">Last</div>
                  <div className="font-display text-xl font-bold">{lastEvent.name}</div>
                  <div className="text-sm text-white/70">{lastEvent.role} · {lastEvent.action} · {lastEvent.at}</div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
